/**
 * What may leave the process for Sentry.
 *
 * Journal entries, prayers, pastoral notes, care messages, and gym health fields stay here.
 * A user id is the only identity we keep. Emails, tokens, and session cookies are removed
 * even when they are embedded in a URL or an error string.
 */

const REDACTED = "[Filtered]";

/** Word parts that are personal or secret on their own. */
const SENSITIVE_PARTS = new Set([
  "journal",
  "prayer",
  "reflection",
  "note",
  "notes",
  "care",
  "chat",
  "thread",
  "dm",
  "health",
  "diagnosis",
  "symptom",
  "medication",
  "email",
  "password",
  "secret",
  "token",
  "authorization",
  "cookie",
  "phone",
  "address",
  "comment",
  "testimony",
  "answer",
  "body",
  "text",
  "limitations",
  "weight",
  "height",
  "age",
  "sex",
  "contact",
  "alias",
  "iban",
]);

/** Keys that only become sensitive once the camelCase words are joined. */
const SENSITIVE_COMPOUNDS = new Set([
  "fullname",
  "firstname",
  "lastname",
  "displayname",
  "otherchurchname",
  "forname",
  "weightkg",
  "heightcm",
  "ipaddress",
  "answernote",
  "accesstoken",
  "refreshtoken",
  "idtoken",
  "apikey",
  "journalkey",
  "secretskey",
  "setcookie",
  "plaintext",
  "pastoralnote",
  "pastornote",
  "caremessage",
  "chatmessage",
  "journalentry",
  "pastoralmessage",
]);

const LOG_LEVELS = new Set(["warn", "error", "fatal"]);

const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const BEARER = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi;
const JWT = /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g;
const SECRET_QUERY = /([?&](?:cookie|token|code|email|state|session)=)[^&\s]*/gi;

export function traceSampleRate(environment: string | undefined): number {
  // Dev and unit tests keep every trace. Deployed environments sample so the free quota survives health checks and normal traffic.
  return environment === "development" || environment === "test" ? 1 : 0.1;
}

export function keepSentryLog(level: string): boolean {
  return LOG_LEVELS.has(level);
}

/** Shared SDK data-collection block. Request bodies, cookies, and inferred user info stay off. */
export function sentryDataCollection() {
  return {
    userInfo: false as const,
    cookies: false as const,
    httpHeaders: false as const,
    httpBodies: [] as [],
    urlQueryParams: { deny: ["cookie", "token", "code", "email", "state", "session"] },
    databaseQueryData: false as const,
    stackFrameVariables: false as const,
  };
}

export function sensitiveKey(key: string): boolean {
  const parts = keyParts(key);
  if (parts.some((part) => SENSITIVE_PARTS.has(part))) return true;
  return SENSITIVE_COMPOUNDS.has(parts.join(""));
}

export function scrubString(value: string): string {
  return value.replace(EMAIL, REDACTED).replace(BEARER, "Bearer [Filtered]").replace(JWT, REDACTED).replace(SECRET_QUERY, "$1[Filtered]");
}

/**
 * Copy `value` with sensitive fields replaced.
 * A top-level `message` (the error or log text) is kept, with emails and tokens removed.
 * `message` next to a journal/prayer/care payload is dropped with the rest of that payload.
 */
export function scrubSentry<T>(value: T): T {
  return scrub(value, "") as T;
}

/** Scrub breadcrumb payloads (XHR/fetch/console) before they leave the client or server. */
export function scrubSentryBreadcrumb<T>(breadcrumb: T): T {
  return scrubSentry(breadcrumb) as T;
}

function scrub(value: unknown, parentKey: string): unknown {
  if (typeof value === "string") return scrubString(value);
  if (typeof value !== "object" || value === null) return value;
  if (value instanceof Date) return value;
  if (Array.isArray(value)) return value.map((item) => scrub(item, parentKey));

  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  const payload = parentKey === "data" || parentKey === "request" || parentKey === "extra" || keys.some((key) => sensitiveKey(key));
  const redactBareName = keys.some((key) => keyParts(key).includes("note"));
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    if ((parentKey === "request" && key === "data") || (redactBareName && keyParts(key).includes("name") && keyParts(key).length === 1)) {
      out[key] = REDACTED;
      continue;
    }
    if (key === "message" && (parentKey === "data" || parentKey === "extra" || parentKey === "metadata")) {
      out[key] = REDACTED;
      continue;
    }
    if (key === "message" && !payload) {
      out[key] = scrub(record[key], key);
      continue;
    }
    if ((key === "content" || key === "text") && (parentKey === "data" || parentKey === "extra" || payload)) {
      out[key] = REDACTED;
      continue;
    }
    if (sensitiveKey(key)) {
      out[key] = REDACTED;
      continue;
    }
    out[key] = scrub(record[key], key);
  }
  return out;
}

function keyParts(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}
